/**
 * API Utilities Module
 *
 * This module provides utility functions for API operations:
 * - Error handling and transformation
 * - URL building and query string handling
 * - Response parsing
 * - Retry logic
 * - Request cancellation helpers
 */

// ============================================================================
// Error Handling Utilities
// ============================================================================

import type { ApiError, ApiErrorCode } from '../types/index';

/**
 * Maps HTTP status codes to API error codes
 */
export function getErrorCodeFromStatus(status: number): ApiErrorCode {
  switch (status) {
    case 401:
      return 'UNAUTHORIZED';
    case 403:
      return 'FORBIDDEN';
    case 404:
      return 'NOT_FOUND';
    case 422:
      return 'VALIDATION_ERROR';
    case 429:
      return 'RATE_LIMITED';
    default:
      if (status >= 500) {
        return 'SERVER_ERROR';
      }
      return 'UNKNOWN_ERROR';
  }
}

/**
 * Creates a structured API error from various error types
 */
export function createApiError(
  status: number,
  message: string,
  details?: Record<string, unknown>,
  originalError?: Error
): ApiError {
  return {
    code: getErrorCodeFromStatus(status),
    status,
    message,
    details,
    originalError,
  };
}

/**
 * Creates a network error
 */
export function createNetworkError(originalError?: Error): ApiError {
  return {
    code: 'NETWORK_ERROR',
    status: 0,
    message: 'Network error. Please check your internet connection.',
    originalError,
  };
}

/**
 * Creates a timeout error
 */
export function createTimeoutError(timeout: number): ApiError {
  return {
    code: 'TIMEOUT_ERROR',
    status: 0,
    message: `Request timed out after ${timeout}ms`,
  };
}

/**
 * Checks if an error is an API error
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'status' in error &&
    'message' in error
  );
}

/**
 * Checks if an error is a network error
 */
export function isNetworkError(error: unknown): boolean {
  return isApiError(error) && error.code === 'NETWORK_ERROR';
}

/**
 * Checks if an error is an authentication error
 */
export function isAuthError(error: unknown): boolean {
  return isApiError(error) && (error.code === 'UNAUTHORIZED' || error.code === 'FORBIDDEN');
}

/**
 * Checks if an error is retryable
 */
export function isRetryableError(error: unknown): boolean {
  if (!isApiError(error)) {
    return false;
  }
  // Retry on network errors, timeouts, and server errors
  return (
    error.code === 'NETWORK_ERROR' ||
    error.code === 'TIMEOUT_ERROR' ||
    error.code === 'SERVER_ERROR'
  );
}

// ============================================================================
// URL Utilities
// ============================================================================

/**
 * Builds a URL with query parameters
 */
export function buildUrl(
  baseUrl: string,
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  // Ensure path starts with /
  const normalizedPath = path.startsWith('/') ? path : `/${path}`;
  
  // Build base URL
  const url = new URL(normalizedPath, baseUrl);
  
  // Add query parameters
  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        url.searchParams.append(key, String(value));
      }
    });
  }
  
  return url.toString();
}

/**
 * Parses query parameters from a URL
 */
export function parseQueryParams(url: string): Record<string, string> {
  const urlObj = new URL(url);
  const params: Record<string, string> = {};
  
  urlObj.searchParams.forEach((value, key) => {
    params[key] = value;
  });
  
  return params;
}

// ============================================================================
// Response Utilities
// ============================================================================

/**
 * Safely parses JSON response
 */
export async function parseJsonResponse<T>(response: Response): Promise<T> {
  const text = await response.text();
  
  if (!text) {
    return {} as T;
  }
  
  try {
    return JSON.parse(text) as T;
  } catch {
    throw createApiError(
      response.status,
      'Invalid JSON response from server',
      { responseText: text.substring(0, 200) }
    );
  }
}

/**
 * Extracts error message from response
 */
export async function extractErrorMessage(response: Response): Promise<string> {
  try {
    const data = await parseJsonResponse<{ message?: string; detail?: string; error?: string }>(
      response
    );
    return data.message ?? data.detail ?? data.error ?? `Request failed with status ${response.status}`;
  } catch {
    return `Request failed with status ${response.status}`;
  }
}

// ============================================================================
// Retry Utilities
// ============================================================================

/**
 * Default retry delay calculation (exponential backoff)
 */
export function calculateRetryDelay(
  attemptIndex: number,
  baseDelay = 1000,
  maxDelay = 30000
): number {
  const delay = Math.min(baseDelay * Math.pow(2, attemptIndex), maxDelay);
  // Add jitter (±10%)
  const jitter = delay * 0.1 * (Math.random() * 2 - 1);
  return Math.round(delay + jitter);
}

/**
 * Delays execution for a specified time
 */
export function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    setTimeout(resolve, ms);
  });
}

// ============================================================================
// Request Cancellation Utilities
// ============================================================================

/**
 * Creates an abort controller with timeout
 */
export function createAbortControllerWithTimeout(
  timeout: number
): { controller: AbortController; timeoutId: ReturnType<typeof setTimeout> } {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  
  return { controller, timeoutId };
}

/**
 * Clears the timeout and returns the signal
 */
export function clearAbortTimeout(
  timeoutId: ReturnType<typeof setTimeout>
): void {
  clearTimeout(timeoutId);
}
